import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, MapPin, Phone, EnvelopeSimple, ShieldCheck, Clock, Lightning, CurrencyDollar, Recycle } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";
import QuoteTool from "@/components/QuoteTool";
import ItemCalculator from "@/components/ItemCalculator";
import WorkPreview from "@/components/WorkPreview";
import { CtaButtons } from "@/components/PageShell";
import { GoogleRatingBadge, GoogleReviewCards } from "@/components/GoogleReviews";
import { PaymentMethods } from "@/components/PaymentMethods";
import usePricing from "@/hooks/usePricing";
import { fillPrices } from "@/priceTokens";
import { SITE, HOME_TITLE, HOME_DESCRIPTION, PHONE_DISPLAY, PHONE_TEL } from "@/seoData";
import { TOP_SERVICES, SITE_PAGES, FEATURED_AREAS, BADGES } from "@/siteContent";

const BADGE_ICONS = [ShieldCheck, Clock, Lightning, CurrencyDollar, Recycle];

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`px-5 lg:px-8 ${className}`}><div className="max-w-7xl mx-auto">{children}</div></section>
);
const H2 = ({ children }) => <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl text-[#15306E]">{children}</h2>;

const HeroEstimate = ({ min }) => (
  <section id="estimate" className="px-5 lg:px-8 pt-28 sm:pt-44 lg:pt-52 pb-14 bg-[#0B1E45] text-white">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 text-[#5DB82F] font-display font-bold uppercase tracking-widest text-xs mb-4">
          <MapPin size={14} weight="fill" /> Serving all of San Diego County
        </div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.98]" data-testid="home-h1">
          Junk Removal in <span className="text-[#5DB82F]">San Diego</span>
        </h1>
        <p data-speakable className="mt-5 text-lg text-white/80 leading-relaxed">
          Same-day junk removal and hauling for homes and businesses. Upfront prices from {min}, open 24/7. Get your price below and book in a minute.
        </p>
        <GoogleRatingBadge />
        <CtaButtons prefix="hero" />
      </div>
      <div className="mt-10 grid lg:grid-cols-2 gap-6 items-stretch" data-testid="home-quote-tools">
        <QuoteTool />
        <ItemCalculator />
      </div>
      <GoogleReviewCards />
      <div className="max-w-3xl mx-auto mt-6 bg-white/95 rounded-lg px-5 py-4"><PaymentMethods /></div>
    </div>
  </section>
);

const TrustBar = () => (
  <div className="bg-[#15306E] py-5" data-testid="trust-marquee">
    <Marquee gradient={false} speed={45} pauseOnHover>
      {[...BADGES, ...BADGES].map((t, i) => {
        const Icon = BADGE_ICONS[i % BADGES.length];
        return (
          <div key={i} className="flex items-center gap-2.5 mx-8 text-white/90">
            <Icon size={20} weight="duotone" className="text-[#5DB82F]" />
            <span className="font-display font-semibold tracking-wide uppercase text-sm">{t}</span>
          </div>
        );
      })}
    </Marquee>
  </div>
);

const ServicesStrip = () => (
  <Section id="services" className="py-14">
    <H2>What we haul</H2>
    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
      {TOP_SERVICES.map((s) => (
        <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`} className="group border border-[#E4E4E7] rounded-sm px-4 py-4 flex items-center justify-between gap-2 hover:border-[#46A01E] transition-colors">
          <span className="font-display font-bold text-[#15306E] text-sm sm:text-base">{s.name}</span>
          <ArrowRight size={16} weight="bold" className="text-[#46A01E] group-hover:translate-x-1 transition-transform shrink-0" />
        </Link>
      ))}
    </div>
  </Section>
);

const PagesGrid = () => (
  <Section className="py-14">
    <H2>Everything else, one tap away</H2>
    <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="home-pages-grid">
      {SITE_PAGES.map((p) => (
        <Link key={p.to} to={p.to} data-testid={`home-page-link-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group bg-[#F4FAF0] border border-[#E4E4E7] rounded-sm p-5 hover:border-[#46A01E] hover:-translate-y-0.5 transition-all">
          <span className="font-display font-extrabold text-lg text-[#15306E] flex items-center gap-2">{p.label} <ArrowRight size={16} weight="bold" className="text-[#46A01E] group-hover:translate-x-1 transition-transform" /></span>
          <span className="block text-sm text-[#52525B] mt-1">{p.desc}</span>
        </Link>
      ))}
    </div>
  </Section>
);

const AreasStrip = () => (
  <Section id="areas" className="py-14 bg-[#15306E] text-white">
    <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl">North San Diego & North County</h2>
    <p className="text-white/70 mt-2">We cover all of San Diego County, with the fastest pickup across North County.</p>
    <div className="mt-6 flex flex-wrap gap-2.5">
      {FEATURED_AREAS.map((a) => (
        <Link key={a.slug} to={`/junk-removal/${a.slug}`} data-testid={`home-area-${a.slug}`} className="flex items-center gap-1.5 border border-white/25 px-3 py-1.5 rounded-sm text-sm text-white/90 hover:border-[#5DB82F] hover:text-white transition-colors">
          <MapPin size={14} weight="fill" className="text-[#5DB82F]" /> {a.name}
        </Link>
      ))}
      <Link to="/service-areas" data-testid="home-areas-all" className="px-3 py-1.5 text-sm font-bold text-[#5DB82F] hover:underline">All service areas →</Link>
    </div>
  </Section>
);

const HomeCta = () => (
  <Section className="py-14 bg-[#F4F4F5]">
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 lg:p-10 grid lg:grid-cols-2 gap-8 items-center">
      <div>
        <H2>Prefer we call you?</H2>
        <p className="mt-3 text-[#52525B]">Send a quick request and we'll text or call back with an upfront price.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href={`tel:${PHONE_TEL}`} data-testid="cta-call-button" className="flex items-center gap-2 bg-[#46A01E] text-white font-display font-extrabold uppercase tracking-wide px-6 py-3.5 rounded-sm hover:bg-[#3A8418] transition-colors"><Phone size={18} weight="fill" /> {PHONE_DISPLAY}</a>
          <a href="mailto:office@cleanout247.com" data-testid="cta-email-button" className="flex items-center gap-2 border border-[#15306E] text-[#15306E] font-display font-bold uppercase tracking-wide px-6 py-3.5 rounded-sm hover:bg-[#F4F4F5] transition-colors"><EnvelopeSimple size={18} weight="fill" /> Email</a>
        </div>
      </div>
      <QuoteForm variant="cta" />
    </div>
  </Section>
);

export default function Landing() {
  const location = useLocation();
  const pricing = usePricing();

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
  }, [location]);

  return (
    <div className="bg-white text-[#0A0A0A] overflow-x-hidden">
      <Seo title={HOME_TITLE} description={HOME_DESCRIPTION} canonical={`${SITE}/`}
        jsonLd={[{ "@context": "https://schema.org", "@type": "WebSite", "@id": SITE + "/#website", name: "CleanOut247", url: SITE + "/", publisher: { "@id": SITE + "/#business" } }, { "@context": "https://schema.org", "@type": "WebPage", "@id": SITE + "/#webpage", url: SITE + "/", name: "CleanOut247 — Junk Removal & Hauling in San Diego", speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "[data-speakable]"] }, about: { "@id": SITE + "/#business" } }]} />
      <Header />
      <HeroEstimate min={fillPrices("{{min}}", pricing)} />
      <TrustBar />
      <ServicesStrip />
      <WorkPreview />
      <PagesGrid />
      <AreasStrip />
      <HomeCta />
      <Footer />
    </div>
  );
}
